import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';

const API_ROOT = (process.env.REACT_APP_API_URL || 'https://www.sistemasoh.cl/api').replace(/\/api\/?$/, '');

function logoPreviewUrl(path, cacheBust) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const q = cacheBust ? `?v=${cacheBust}` : '';
  return `${API_ROOT}${path.startsWith('/') ? path : `/${path}`}${q}`;
}

/** Reduce peso antes de subir (preview instantánea + upload más rápido). */
async function compressLogoForUpload(file) {
  if (!file.type.startsWith('image/')) return file;
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxW = 320;
      const maxH = 120;
      let { width, height } = img;
      const ratio = Math.min(maxW / width, maxH / height, 1);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          resolve(new File([blob], 'estudio-logo.jpg', { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la imagen'));
    };
    img.src = url;
  });
}

export default function InformeIvaCorreoPage() {
  const [cfg, setCfg] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreviewLocal, setLogoPreviewLocal] = useState('');
  const [logoCacheBust, setLogoCacheBust] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/informes-iva/config/global');
    setCfg(data);
    setTestTo(data.emailOficina || data.smtpUser || '');
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(e.response?.data?.error || e.message));
  }, [load]);

  const payloadFromCfg = (c) => ({
    emailOficina: c.emailOficina,
    autoSyncOnRun: c.autoSyncOnRun,
    syncF29OnRun: c.syncF29OnRun,
    scheduledRunsEnabled: c.scheduledRunsEnabled,
    smtpHost: c.smtpHost,
    smtpPort: c.smtpPort,
    smtpUser: c.smtpUser,
    smtpFrom: c.smtpFrom,
    smtpFromName: c.smtpFromName,
    smtpPass: c.smtpPass,
    estudioNombre: c.estudioNombre,
    estudioLogoUrl: c.estudioLogoUrl,
    brandColor: c.brandColor,
    estudioDireccion: c.estudioDireccion,
    estudioTelefono: c.estudioTelefono,
    estudioWeb: c.estudioWeb,
    estudioContactEmail: c.estudioContactEmail,
    contadorCargoDefault: c.contadorCargoDefault,
    emailMensajeAdicional: c.emailMensajeAdicional,
    emailSubjectTemplate: c.emailSubjectTemplate,
    emailIntroTemplate: c.emailIntroTemplate,
    emailDisclaimerMain: c.emailDisclaimerMain,
    emailDisclaimerFoot: c.emailDisclaimerFoot,
    emailFirmaTemplate: c.emailFirmaTemplate,
    emailReplyToMode: c.emailReplyToMode,
    emailReplyToFixed: c.emailReplyToFixed,
    emailShowResumen: c.emailShowResumen,
    emailAttachPdf: c.emailAttachPdf,
    emailShowContador: c.emailShowContador
  });

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      await api.put('/informes-iva/config/global', payloadFromCfg(cfg));
      toast.success('Configuración guardada');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setLogoPreviewLocal(localUrl);
    setLogoUploading(true);
    try {
      const compressed = await compressLogoForUpload(file);
      const fd = new FormData();
      fd.append('logo', compressed);
      const { data } = await api.post('/informes-iva/config/global/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      toast.success('Logo listo');
      setLogoCacheBust(data.v || Date.now());
      setCfg((c) => ({ ...c, estudioLogoUrl: data.estudioLogoUrl }));
      URL.revokeObjectURL(localUrl);
      setLogoPreviewLocal('');
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLogoUploading(false);
    }
  };

  const resetTemplates = async () => {
    if (!window.confirm('¿Restaurar asunto, intro, disclaimers y firma al texto base del estudio?')) return;
    setBusy(true);
    try {
      await api.post('/informes-iva/config/global/reset-email-templates');
      toast.success('Plantillas restauradas');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const testMail = async () => {
    setBusy(true);
    try {
      await save();
      const { data } = await api.post('/informes-iva/config/global/test-email', { to: testTo });
      if (data.ok) toast.success('Correo de prueba enviado');
      else toast.error(data.reason || 'Error al enviar');
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.reason || e.message);
    } finally {
      setBusy(false);
    }
  };

  const logoSrc = useMemo(() => {
    if (logoPreviewLocal) return logoPreviewLocal;
    return logoPreviewUrl(cfg?.estudioLogoUrl, logoCacheBust);
  }, [cfg?.estudioLogoUrl, logoCacheBust, logoPreviewLocal]);

  if (!cfg) {
    return <div className="max-w-3xl mx-auto p-8 text-slate-500">Cargando…</div>;
  }

  const set = (patch) => setCfg({ ...cfg, ...patch });

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <p className="text-sm text-slate-500 mb-1">
        <Link to="/informes-iva" className="text-indigo-600 hover:underline">
          ← Informes IVA
        </Link>
      </p>
      <h1 className="text-2xl font-semibold text-slate-900">Identidad y correo al cliente</h1>
      <p className="text-sm text-slate-600 mt-2 mb-6">
        El correo ya trae el texto base acordado (saludo, resumen automático, PDF, disclaimers). Aquí solo ajusta
        identidad del estudio y, si quiere, un párrafo extra o detalles avanzados.
      </p>

      <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4 text-sm mb-6">
        <h2 className="font-semibold text-slate-800">Lo esencial (estudio)</h2>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Nombre del estudio (firma y remitente)</label>
          <input
            className="w-full border border-slate-300 rounded-md px-3 py-2"
            placeholder="Estudio Contable OH"
            value={cfg.estudioNombre || ''}
            onChange={(e) => set({ estudioNombre: e.target.value, smtpFromName: e.target.value || cfg.smtpFromName })}
          />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-[200px]">
            <label className="block text-xs font-medium text-slate-600 mb-1">Logo</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              disabled={logoUploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadLogo(f);
                e.target.value = '';
              }}
            />
            <p className="text-xs text-slate-500 mt-1">
              Se optimiza al subir (máx. 320×120). {logoUploading ? 'Subiendo…' : 'Vista previa al instante.'}
            </p>
          </div>
          <div
            className="flex items-center justify-center min-h-[56px] min-w-[140px] border border-dashed border-slate-300 rounded-lg bg-slate-50 p-2"
            style={{ backgroundColor: cfg.brandColor ? `${cfg.brandColor}18` : undefined }}
          >
            {logoSrc ? (
              <img src={logoSrc} alt="Logo" className="max-h-14 max-w-[180px] object-contain" />
            ) : (
              <span className="text-xs text-slate-400">Sin logo</span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Color principal</label>
            <input
              type="color"
              className="w-full h-10 border border-slate-300 rounded-md"
              value={cfg.brandColor || '#1F4E78'}
              onChange={(e) => set({ brandColor: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Teléfono estudio</label>
            <input
              className="w-full border border-slate-300 rounded-md px-3 py-2"
              value={cfg.estudioTelefono || ''}
              onChange={(e) => set({ estudioTelefono: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Párrafo adicional (opcional, se agrega después del saludo)
          </label>
          <textarea
            className="w-full border border-slate-300 rounded-md px-3 py-2 min-h-[72px]"
            placeholder="Ej. Durante este mes recomendamos provisionar aprox. $600.000 para obligaciones mensuales."
            value={cfg.emailMensajeAdicional || ''}
            onChange={(e) => set({ emailMensajeAdicional: e.target.value })}
          />
          <p className="text-xs text-slate-500 mt-1">
            No necesita variables: el resumen con montos (IVA, PPM, total) se genera solo al enviar.
          </p>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4 text-sm mb-6">
        <h2 className="font-semibold text-slate-800">SMTP</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            className="border border-slate-300 rounded-md px-3 py-2 md:col-span-2"
            placeholder="Servidor SMTP"
            value={cfg.smtpHost || ''}
            onChange={(e) => set({ smtpHost: e.target.value })}
          />
          <input
            type="number"
            className="border border-slate-300 rounded-md px-3 py-2"
            placeholder="Puerto"
            value={cfg.smtpPort ?? 587}
            onChange={(e) => set({ smtpPort: Number(e.target.value) })}
          />
          <input
            type="email"
            className="border border-slate-300 rounded-md px-3 py-2"
            placeholder="Correo remitente (email@dominio.cl)"
            value={cfg.smtpFrom || ''}
            onChange={(e) => set({ smtpFrom: e.target.value })}
          />
          <input
            className="border border-slate-300 rounded-md px-3 py-2"
            placeholder="Usuario SMTP"
            value={cfg.smtpUser || ''}
            onChange={(e) => set({ smtpUser: e.target.value })}
          />
          <input
            type="password"
            className="border border-slate-300 rounded-md px-3 py-2"
            placeholder="Contraseña SMTP"
            onChange={(e) => set({ smtpPass: e.target.value || '********' })}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Al responder (Reply-To)</label>
            <select
              className="w-full border border-slate-300 rounded-md px-3 py-2"
              value={cfg.emailReplyToMode || 'CONTADOR_CLIENTE'}
              onChange={(e) => set({ emailReplyToMode: e.target.value })}
            >
              <option value="CONTADOR_CLIENTE">Correo del contador IVA de la empresa</option>
              <option value="FIJO">Correo fijo</option>
              <option value="NONE">Sin Reply-To</option>
            </select>
          </div>
          {cfg.emailReplyToMode === 'FIJO' && (
            <input
              className="border border-slate-300 rounded-md px-3 py-2"
              placeholder="Reply-To fijo"
              value={cfg.emailReplyToFixed || ''}
              onChange={(e) => set({ emailReplyToFixed: e.target.value })}
            />
          )}
        </div>
        <input
          className="w-full border border-slate-300 rounded-md px-3 py-2"
          placeholder="CC oficina en cada envío"
          value={cfg.emailOficina || ''}
          onChange={(e) => set({ emailOficina: e.target.value })}
        />
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cfg.emailShowResumen !== false}
              onChange={(e) => set({ emailShowResumen: e.target.checked })}
            />
            Resumen tributario en el correo
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cfg.emailAttachPdf !== false}
              onChange={(e) => set({ emailAttachPdf: e.target.checked })}
            />
            Adjuntar PDF
          </label>
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-1">Enviar prueba a</label>
          <input
            className="w-full border border-slate-300 rounded-md px-3 py-2"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
        </div>
      </section>

      <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm mb-6">
        <button
          type="button"
          className="font-medium text-indigo-700 hover:underline"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          {showAdvanced ? '▾ Ocultar' : '▸ Editar plantillas completas (avanzado)'}
        </button>
        {showAdvanced && (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-slate-600">
              Ya vienen precargadas con el texto que compartiste. Solo edite si necesita cambiar el tono completo.
              Variables opcionales: {'{{cliente.saludo}}'}, {'{{periodo.nombre}}'}, {'{{contador.nombre}}'}, etc.
            </p>
            <label className="block text-xs font-medium">Asunto</label>
            <input
              className="w-full border rounded-md px-3 py-2"
              value={cfg.emailSubjectTemplate || ''}
              onChange={(e) => set({ emailSubjectTemplate: e.target.value })}
            />
            <label className="block text-xs font-medium">Introducción</label>
            <textarea
              className="w-full border rounded-md px-3 py-2 min-h-[100px]"
              value={cfg.emailIntroTemplate || ''}
              onChange={(e) => set({ emailIntroTemplate: e.target.value })}
            />
            <label className="block text-xs font-medium">Disclaimer principal</label>
            <textarea
              className="w-full border rounded-md px-3 py-2 min-h-[72px]"
              value={cfg.emailDisclaimerMain || ''}
              onChange={(e) => set({ emailDisclaimerMain: e.target.value })}
            />
            <label className="block text-xs font-medium">Disclaimer secundario</label>
            <textarea
              className="w-full border rounded-md px-3 py-2 min-h-[48px]"
              value={cfg.emailDisclaimerFoot || ''}
              onChange={(e) => set({ emailDisclaimerFoot: e.target.value })}
            />
            <label className="block text-xs font-medium">Firma</label>
            <textarea
              className="w-full border rounded-md px-3 py-2 min-h-[100px]"
              value={cfg.emailFirmaTemplate || ''}
              onChange={(e) => set({ emailFirmaTemplate: e.target.value })}
            />
            <button
              type="button"
              className="text-sm text-slate-600 underline"
              disabled={busy}
              onClick={resetTemplates}
            >
              Restaurar texto base del estudio
            </button>
          </div>
        )}
      </section>

      <p className="text-xs text-slate-500 mb-4">
        Contador por empresa: en el panel Informes IVA → <strong>Email del cliente y opciones avanzadas</strong> →
        sección «Contador responsable».
      </p>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
          onClick={save}
        >
          Guardar
        </button>
        <button
          type="button"
          disabled={busy}
          className="px-4 py-2 border border-slate-300 rounded-md text-sm disabled:opacity-50"
          onClick={testMail}
        >
          Enviar prueba SMTP
        </button>
      </div>
    </div>
  );
}
